/* =============================================================================
   main.js - wires the form to the validator and the preview.
   No module level state: everything mutable lives in the closure that init()
   creates for one configurator instance. No localStorage.

   Shopify note: the element ids below are looked up inside the root element
   that init() receives. If a theme ever renders this section twice on one
   page, suffix the ids with the section id (ids must stay unique for the
   label / aria-describedby wiring) and pass the matching root.
   ========================================================================== */
var PlateConfigurator = (function (cfg, val, preview) {
  'use strict';

  /* --- cart payload (pure) -------------------------------------------------
     The keys are German because this is what the shop backend receives.      */
  function buildCartPayload(rawOrState) {
    var state = val.normalize(rawOrState);
    var size = cfg.getSize(state.sizeId) || cfg.SIZES[0];
    var type = cfg.getType(state.type) || cfg.TYPES[0];
    return {
      kennzeichen: val.formatKennzeichen(state),
      groesse: size.id,
      typ: type.label,
      saison: val.formatSeason(state),
      menge: state.quantity
    };
  }

  /* --- DOM helpers --------------------------------------------------------- */

  function option(value, label) {
    var el = document.createElement('option');
    el.value = value;
    el.textContent = label;
    return el;
  }

  function fill(select, items, valueKey, labelKey, selected) {
    if (!select) { return; }
    select.textContent = '';
    for (var i = 0; i < items.length; i++) {
      var el = option(items[i][valueKey], items[i][labelKey]);
      if (String(items[i][valueKey]) === String(selected)) { el.selected = true; }
      select.appendChild(el);
    }
  }

  // Writes the normalized value back into the field without throwing the caret
  // to the end: the caret only moves by however many characters were dropped.
  function syncInput(input, value) {
    if (!input || input.value === value) { return; }
    var start = input.selectionStart;
    var end = input.selectionEnd;
    var delta = value.length - input.value.length;
    var focused = (document.activeElement === input);
    input.value = value;
    if (focused && start !== null) {
      var from = Math.max(0, Math.min(value.length, start + Math.min(0, delta)));
      var to = Math.max(from, Math.min(value.length, end + Math.min(0, delta)));
      try { input.setSelectionRange(from, to); } catch (e) { /* type may not allow it */ }
    }
  }

  // Nothing is drawn behind the state icon, so if the file 404s the seal would
  // be left with a hole. Swapping the failed image for the grey dashed circle
  // keeps the promise that a broken image never reaches a product page.
  function sealPlaceholderFor(icon) {
    var x = parseFloat(icon.getAttribute('x')) || 0;
    var y = parseFloat(icon.getAttribute('y')) || 0;
    var size = parseFloat(icon.getAttribute('width')) || 0;
    var circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', String(x + size / 2));
    circle.setAttribute('cy', String(y + size / 2));
    circle.setAttribute('r', String(size / 2));
    circle.setAttribute('fill', cfg.COLORS.sealFill);
    circle.setAttribute('stroke', cfg.COLORS.sealOutline);
    circle.setAttribute('stroke-width', String(cfg.METRICS.sealStrokeWidth));
    circle.setAttribute('stroke-dasharray', cfg.METRICS.sealDashArray);
    circle.setAttribute('class', 'kfz-seal-placeholder');
    return circle;
  }

  function guardSealIcons(container) {
    if (!container || !container.querySelectorAll) { return; }
    var icons = container.querySelectorAll('.kfz-seal-icon');
    for (var i = 0; i < icons.length; i++) {
      icons[i].addEventListener('error', function (event) {
        var icon = event.currentTarget;
        if (!icon || !icon.parentNode) { return; }
        icon.parentNode.replaceChild(sealPlaceholderFor(icon), icon);
      });
    }
  }

  function setError(node, message) {
    if (!node) { return; }
    node.textContent = message || '';
  }

  function markInvalid(input, invalid) {
    if (!input) { return; }
    if (invalid) { input.setAttribute('aria-invalid', 'true'); }
    else { input.removeAttribute('aria-invalid'); }
  }

  /* --- init ---------------------------------------------------------------- */
  function init(root, options) {
    if (!root) { return null; }
    var settings = options || {};
    // onSubmit defaults to logging the payload; Shopify will pass a real
    // handler that posts to /cart/add.js.
    var onSubmit = settings.onSubmit || function (payload) {
      console.log('add to cart', payload);
    };

    // Shopify renders a section's ids with the section id appended, so the
    // block can appear more than once on a page without duplicate ids. The
    // dev harness passes no suffix and the ids stay plain.
    var suffix = settings.idSuffix || '';
    function byId(id) { return root.querySelector('#' + id + suffix); }

    var refs = {
      form:      byId('kfz-form'),
      cityCode:  byId('kfz-city'),
      letters:   byId('kfz-letters'),
      digits:    byId('kfz-digits'),
      parkingText: byId('kfz-parking-text'),
      parkingRow:  byId('kfz-parking-row'),
      plateRow:    byId('kfz-plate-row'),
      sizeId:    byId('kfz-size'),
      type:      byId('kfz-type'),
      season:    byId('kfz-season'),
      seasonFrom: byId('kfz-season-from'),
      seasonTo:  byId('kfz-season-to'),
      quantity:  byId('kfz-quantity'),
      plate:     byId('kfz-plate'),
      submit:    byId('kfz-submit'),
      reset:     byId('kfz-reset'),
      warnings: {
        cityCode: byId('kfz-warning-cityCode')
      },
      errors: {
        cityCode: byId('kfz-error-cityCode'),
        letters:  byId('kfz-error-letters'),
        digits:   byId('kfz-error-digits'),
        season:   byId('kfz-error-season'),
        parkingText: byId('kfz-error-parkingText'),
        general:  byId('kfz-error-general')
      }
    };

    // Per instance, not global: which fields the user has already worked on.
    // Untouched fields stay quiet, so an untouched form is not a wall of red.
    var touched = { cityCode: false, letters: false, digits: false, season: false, parkingText: false };
    var submitAttempted = false;

    var defaults = cfg.defaults();

    // Which sizes this page offers. The theme passes a list; without one the
    // whole catalogue shows, which is what the local dev harness wants.
    var sizes = cfg.sizesFrom(settings.sizeIds);

    // Reference width for the scaled preview: the widest of the offered sizes,
    // so a page that only sells motorcycle plates still uses its full box.
    var widestSize = (function () {
      var max = 1;
      for (var s = 0; s < sizes.length; s++) {
        if (sizes[s].w > max) { max = sizes[s].w; }
      }
      return max;
    })();

    // The first size in the list is the one the page sells by default, so it
    // carries the word "Standard" - the same way the dropdown reads on the
    // reference shop.
    var sizeOptions = [];
    for (var s = 0; s < sizes.length; s++) {
      sizeOptions.push({
        id: sizes[s].id,
        label: (s === 0) ? ('Standard (' + sizes[s].label + ')') : sizes[s].label
      });
    }

    // Default to the first offered size, not to the first of the catalogue:
    // 520 x 110 mm is not on the menu of a motorcycle page.
    defaults.sizeId = sizes[0].id;

    fill(refs.sizeId, sizeOptions, 'id', 'label', defaults.sizeId);
    fill(refs.type, cfg.TYPES, 'id', 'label', defaults.type);
    fill(refs.seasonFrom, cfg.MONTHS, 'value', 'label', defaults.seasonFrom);
    fill(refs.seasonTo, cfg.MONTHS, 'value', 'label', defaults.seasonTo);
    fill(refs.quantity, cfg.QUANTITIES, 'value', 'label', defaults.quantity);

    // Reads the form. The single source of truth is the DOM, so there is no
    // second copy of the state to keep in sync.
    function readState() {
      return {
        cityCode:   refs.cityCode ? refs.cityCode.value : '',
        letters:    refs.letters ? refs.letters.value : '',
        digits:     refs.digits ? refs.digits.value : '',
        parkingText: refs.parkingText ? refs.parkingText.value : '',
        sizeId:     refs.sizeId ? refs.sizeId.value : defaults.sizeId,
        type:       refs.type ? refs.type.value : defaults.type,
        seasonFrom: refs.seasonFrom ? refs.seasonFrom.value : '',
        seasonTo:   refs.seasonTo ? refs.seasonTo.value : '',
        quantity:   refs.quantity ? refs.quantity.value : defaults.quantity
      };
    }

    function render() {
      var raw = readState();
      var state = val.normalize(raw);

      // Uppercasing and character filtering happen in validate.js, so the
      // field shows exactly what the preview draws.
      syncInput(refs.cityCode, state.cityCode);
      syncInput(refs.letters, state.letters);
      syncInput(refs.digits, state.digits);
      syncInput(refs.parkingText, state.parkingText);

      var type = cfg.getType(state.type) || cfg.TYPES[0];
      // Ein Parkplatzschild traegt eine freie Zeile statt Unterscheidungs-
      // zeichen und Erkennungsnummer, und weder Typ noch Saison gibt es dort.
      // Deshalb tauschen die beiden Bloecke die Sichtbarkeit, statt dass die
      // Seite zwei Formulare vorhaelt.
      var parking = !!(cfg.getSize(state.sizeId) || {}).parking;
      if (refs.parkingRow) { refs.parkingRow.hidden = !parking; }
      if (refs.plateRow) { refs.plateRow.hidden = parking; }
      if (refs.season) { refs.season.hidden = parking || !type.isSeason; }
      // Auf einem Parkplatzschild gibt es nur eine Ausfuehrung, die Auswahl
      // waere eine Liste mit einem Eintrag. Versteckt wird der ganze Block,
      // nicht nur das Auswahlfeld, sonst bliebe die Beschriftung stehen.
      if (refs.type) {
        // Zwei Layouts, zwei Huellen: die Shopify-Optik legt das Auswahlfeld in
        // .ss-type, das schlichte Formular in .kfz-field.
        var typeRow = refs.type.closest ? refs.type.closest('.ss-type, .kfz-field') : null;
        if (typeRow) { typeRow.hidden = parking; }
      }

      // Validate the raw values: normalize() would silently repair a tampered
      // select, and a silent repair is worse than an error message.
      var result = val.validate(raw);
      var messages = val.errorMap(result);
      var warnings = val.warningMap(result);

      var fields = ['cityCode', 'letters', 'digits', 'season', 'parkingText'];
      for (var i = 0; i < fields.length; i++) {
        var field = fields[i];
        var show = touched[field] || submitAttempted;
        var text = (show && messages[field]) ? messages[field][0] : '';
        setError(refs.errors[field], text);
        markInvalid(refs[field], !!text);

        // Warnings share the touched policy but never mark the field invalid
        // and never disable the button.
        if (refs.warnings[field]) {
          setError(refs.warnings[field], (show && warnings[field]) ? warnings[field][0] : '');
        }
      }

      // The general slot is always shown: it only ever carries the total
      // length rule or a value no legitimate interaction can produce.
      setError(refs.errors.general, messages.general ? messages.general.join(' ') : '');

      if (refs.plate) {
        refs.plate.innerHTML = preview.renderPlate(state);
        guardSealIcons(refs.plate);
        // The preview is drawn to scale: a 340 mm plate has to look narrower
        // than a 520 mm one, otherwise every size looks the same and the
        // customer cannot see what changed. The widest size fills the box,
        // all others keep their real proportion to it. The height follows on
        // its own, because the SVG carries the aspect ratio of the viewBox.
        //
        // The factor goes out as a custom property and the stylesheet applies
        // it to the SVG. Setting a width on the wrapper here would override
        // whatever width the theme gives it, and the theme caps the preview.
        var size = cfg.getSize(state.sizeId) || cfg.SIZES[0];
        refs.plate.style.setProperty('--kfz-plate-scale', size.w / widestSize);
        var previewWidths = { 520: 335, 480: 300, 460: 280, 420: 250, 400: 225, 340: 225, 320: 210, 280: 200 };
        var referencePreview = !size.parking && type.id === 'standard' && !!previewWidths[size.w];
        refs.plate.classList.toggle('kfz-plate--reference', referencePreview);
        refs.plate.style.setProperty('--kfz-preview-width', (previewWidths[size.w] || 335) + 'px');
        refs.plate.style.setProperty('--kfz-preview-height', (size.twoLine ? 120 : 60) + 'px');
      }
      if (refs.submit) { refs.submit.disabled = !result.ok; }

      return result;
    }

    function touch(field) {
      return function () {
        touched[field] = true;
        render();
      };
    }

    var textFields = ['cityCode', 'letters', 'digits', 'parkingText'];
    for (var i = 0; i < textFields.length; i++) {
      var input = refs[textFields[i]];
      if (!input) { continue; }
      input.addEventListener('input', touch(textFields[i]));
      input.addEventListener('blur', touch(textFields[i]));
    }

    // Reset clears the plate fields and the touched flags with them, so the
    // form goes back to quiet instead of turning red on empty required fields.
    if (refs.reset) {
      refs.reset.addEventListener('click', function () {
        var fields = ['cityCode', 'letters', 'digits', 'parkingText'];
        for (var i = 0; i < fields.length; i++) {
          if (refs[fields[i]]) { refs[fields[i]].value = ''; }
          touched[fields[i]] = false;
        }
        submitAttempted = false;
        render();
        if (refs.cityCode) { refs.cityCode.focus(); }
      });
    }

    if (refs.sizeId) { refs.sizeId.addEventListener('change', render); }
    if (refs.type) { refs.type.addEventListener('change', render); }
    if (refs.quantity) { refs.quantity.addEventListener('change', render); }
    if (refs.seasonFrom) { refs.seasonFrom.addEventListener('change', touch('season')); }
    if (refs.seasonTo) { refs.seasonTo.addEventListener('change', touch('season')); }

    if (refs.form) {
      refs.form.addEventListener('submit', function (event) {
        event.preventDefault();
        submitAttempted = true;
        var result = render();
        // The button is disabled while invalid, but Enter in a text field can
        // still fire submit - so check again instead of trusting the button.
        if (!result.ok) { return; }
        onSubmit(buildCartPayload(readState()));
      });
    }

    render();

    // Handed back so a Shopify section (or a test page) can drive the instance.
    return { render: render, readState: readState, getPayload: function () {
      return buildCartPayload(readState());
    } };
  }

  return {
    init: init,
    buildCartPayload: buildCartPayload
  };
})(PlateConfig, PlateValidate, PlatePreview);

